import {
  Button,
  Drawer,
  Row,
  Space,
  Form,
  Select,
  Input,
  notification,
} from 'antd';
import {
  useAddImplementMutation,
  useGetAllGpsImplementsQuery,
  useGetImplementAccountingMethodsQuery,
  useUpdateImplementMutation,
} from 'app/api';
import NumberInput from 'app/components/NumberInput/NumberInput';
import ImplementTypeSelect from 'app/components/Select/ImplementTypeSelect/ImplementTypeSelect';
import TaskCategorySelect from 'app/components/Select/TaskCategorySelect/TaskCategorySelect';
import { selectCompanies } from 'app/containers/Company/slice/selectors';
import { useGlobalSlice } from 'app/containers/Global/slice';
import { handleError } from 'app/containers/Global/slice/helpers';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IOption } from 'types';
import { getInteger } from 'utils/helpers';
import { getEditImplementFormInitialValues } from '../../helpers';
import {
  IMPLEMENT_ACCOUNTING_METHODS_MAP,
  IImplementFormValues,
  IAddImplementPayload,
  IImplement,
} from '../../types';

interface IProps {
  isOpen: boolean;
  implement?: IImplement;

  onClose: () => void;
}
const ImplementDrawer: FC<IProps> = ({ isOpen, onClose, implement }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const globalActions = useGlobalSlice().actions;

  const [form] = Form.useForm();
  const envId = useSelector(selectSelectedEnvId) as number;

  // Company Options
  const allCompanies = useSelector(selectCompanies);
  const companyOptions: IOption[] | undefined = allCompanies?.map(company => ({
    value: company.id,
    label: company.name,
  }));

  const [selectedCompanyId, setSelectedCompanyId] = useState<
    number | undefined
  >();

  // GPS Implement Options
  const {
    currentData: allGpsImplements,
    isFetching: isGpsImplementsLoading,
    error: gpsImplementError,
  } = useGetAllGpsImplementsQuery({ envId }, { skip: !envId });

  const gpsImplementOptions: IOption[] | undefined = allGpsImplements?.map(
    gpsImplement => ({
      value: gpsImplement.id,
      label: gpsImplement.name,
      companyId: gpsImplement.companyId,
      disabled:
        implement?.id !== gpsImplement.garageImplementId &&
        gpsImplement.assigned,
    }),
  );

  // Reset gps implement if error on loading options
  useEffect(() => {
    if (gpsImplementError) {
      form.setFieldsValue({ gpsImplementId: undefined });
      handleError(gpsImplementError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpsImplementError]);

  // Accounting Methods Options
  const {
    data: accountingMethods,
    isFetching: isAccountingMethodsLoading,
  } = useGetImplementAccountingMethodsQuery();

  const accountingMethodOptions:
    | IOption<number>[]
    | undefined = accountingMethods?.map(method => ({
    value: method.id,
    label: method.name,
  }));

  const [selectedAccountingMethods, setSelectedAccountingMethods] = useState<
    number[]
  >([]);

  // Set edit implement data on open
  useEffect(() => {
    if (isOpen && implement) {
      const values = getEditImplementFormInitialValues(implement);
      setSelectedAccountingMethods(values.accountingMethods);
      setSelectedCompanyId(values.companyId);
      form.setFieldsValue(values);
    }
  }, [implement, isOpen, form]);

  // Clear form on close
  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
      setSelectedCompanyId(undefined);
      setSelectedAccountingMethods([]);
    }
  }, [isOpen, form]);

  const [
    addImplement,
    { isLoading: isAddSubmitLoading },
  ] = useAddImplementMutation();

  const [
    updateImplement,
    { isLoading: isUpdateSubmitLoading },
  ] = useUpdateImplementMutation();

  const isSubmitLoading = isAddSubmitLoading || isUpdateSubmitLoading;

  const onFinish = async (values: IImplementFormValues) => {
    try {
      if (!envId || isSubmitLoading) {
        return;
      }
      const {
        haSellPrice,
        haCostPrice,
        motoHourCostPrice,
        motoHourSellPrice,
        width,
        ...formValues
      } = values;

      const methods: {
        costPrice: number | undefined;
        id: number;
        sellPrice: number | undefined;
      }[] =
        values.accountingMethods?.map(methodId => {
          if (methodId === IMPLEMENT_ACCOUNTING_METHODS_MAP.ha) {
            return {
              costPrice: getInteger(values.haCostPrice),
              sellPrice: getInteger(values.haSellPrice),
              id: methodId,
            };
          }
          return {
            costPrice: getInteger(values.motoHourCostPrice),
            sellPrice: getInteger(values.motoHourSellPrice),
            id: methodId,
          };
        }) || [];

      const payload: IAddImplementPayload = {
        ...formValues,
        envId,
        width: getInteger(width),
        year: values.year ? Number(values.year) : undefined,
        accountingMethods: methods,
      };

      let successMsg = '';
      if (implement) {
        await updateImplement({
          ...payload,
          implementId: implement.id,
        }).unwrap();
        successMsg = t(
          'Implement.Drawer.SuccessfullyUpdated',
          'Implement successfully updated',
        );
      } else {
        await addImplement(payload).unwrap();
        successMsg = t(
          'Implement.Drawer.ImplementSuccessfullyAdded',
          'Implement successfully added',
        );
      }

      onClose();
      notification.success({
        message: successMsg,
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch(globalActions.handleError(error));
    }
  };

  const title = implement
    ? t('Implement.Drawer.EditTitle', 'Edit implement')
    : t('Implement.Drawer.Title', 'Add implement');

  return (
    <Drawer
      title={title}
      visible={isOpen}
      forceRender={true}
      onClose={onClose}
      destroyOnClose
      placement="right"
      width={400}
      footer={
        <Row justify="end">
          <Space>
            <Button onClick={onClose}>{t('Common.Cancel', 'Cancel')}</Button>

            <Button
              loading={isSubmitLoading}
              form="implementForm"
              key="submit"
              htmlType="submit"
              type="primary"
            >
              {t('Common.Save', 'Save')}
            </Button>
          </Space>
        </Row>
      }
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{ layout: 'vertical' }}
        requiredMark={true}
        id="implementForm"
        onFinish={onFinish}
      >
        <Form.Item
          label={t('Transport.Type', 'Type')}
          name="typeId"
          rules={[{ required: true }]}
        >
          <ImplementTypeSelect
            bordered={true}
            placeholder={t('Transport.Type', 'Type')}
            dropdownMatchSelectWidth={false}
            allowClear={true}
          />
        </Form.Item>

        <Form.Item
          label={t('Transport.Model', 'Model')}
          name="model"
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Transport.Model', 'Model')} />
        </Form.Item>

        <Form.Item
          label={t('Transport.Ownership', 'Ownership')}
          name="companyId"
          rules={[{ required: true }]}
        >
          <Select
            bordered={true}
            placeholder={t('Transport.Ownership', 'Ownership')}
            dropdownMatchSelectWidth={false}
            options={companyOptions}
            value={selectedCompanyId}
            onChange={value => setSelectedCompanyId(value)}
            disabled={Boolean(implement)}
            allowClear
          />
        </Form.Item>

        <Form.Item
          label={'Bluetooth ID'}
          name="gpsImplementId"
          rules={[{ required: false }]}
        >
          <Select
            onChange={(value, option) => {
              if ('companyId' in option && !selectedCompanyId) {
                setSelectedCompanyId(option.companyId);
                form.setFieldsValue({ companyId: option.companyId });
              }
            }}
            bordered={true}
            placeholder={'Bluetooth ID'}
            dropdownMatchSelectWidth={false}
            options={gpsImplementOptions}
            loading={isGpsImplementsLoading}
            allowClear={true}
          />
        </Form.Item>
        <Space size={36}>
          <Form.Item
            label={t('Transport.Year', 'Year of production')}
            name="year"
          >
            <NumberInput
              placeholder={t('Transport.Year', 'Year of production')}
            />
          </Form.Item>

          <Form.Item
            label={t('Implement.ImpementWidth', 'Implement width (m)')}
            name="width"
          >
            <NumberInput
              placeholder={t('Implement.ImpementWidth', 'Implement width (m)')}
            />
          </Form.Item>
        </Space>

        <Form.Item
          label={t('Implement.MainPerformingTask', 'Main performing task')}
          name="defaultTaskCategoryId"
          rules={[{ required: true }]}
        >
          <TaskCategorySelect
            bordered={true}
            placeholder={t(
              'Implement.MainPerformingTask',
              'Main performing task',
            )}
            dropdownMatchSelectWidth={false}
            allowClear={true}
          />
        </Form.Item>

        <Form.Item
          label={t('Implement.PerformingTask', 'Performing task')}
          name="taskCategoryIds"
        >
          <TaskCategorySelect
            mode="multiple"
            bordered={true}
            placeholder={t('Implement.PerformingTask', 'Performing task')}
            dropdownMatchSelectWidth={false}
            allowClear={true}
          />
        </Form.Item>

        <Form.Item
          label={t('Transport.CalculationMethod', 'Calculation method')}
          name="accountingMethods"
        >
          <Select
            mode="multiple"
            bordered={true}
            placeholder={t('Transport.CalculationMethod', 'Calculation method')}
            dropdownMatchSelectWidth={false}
            options={accountingMethodOptions}
            value={selectedAccountingMethods}
            onChange={value => setSelectedAccountingMethods(value)}
            allowClear={true}
            loading={isAccountingMethodsLoading}
          />
        </Form.Item>

        {selectedAccountingMethods.includes(
          IMPLEMENT_ACCOUNTING_METHODS_MAP.motoHours,
        ) && (
          <>
            <Form.Item
              label={t('Transport.MotoHourCostPrice', 'Motohour cost price')}
              name="motoHourCostPrice"
            >
              <NumberInput
                placeholder={t(
                  'Transport.MotoHourCostPrice',
                  'Motohour cost price',
                )}
              />
            </Form.Item>

            <Form.Item
              label={t('Transport.MotoHourSellPrice', 'Motohour sell price')}
              name="motoHourSellPrice"
            >
              <NumberInput
                placeholder={t(
                  'Transport.MotoHourSellPrice',
                  'Motohour sell price',
                )}
              />
            </Form.Item>
          </>
        )}

        {selectedAccountingMethods.includes(
          IMPLEMENT_ACCOUNTING_METHODS_MAP.ha,
        ) && (
          <>
            <Form.Item
              label={t('Transport.HaCostPrice', 'Ha cost price')}
              name="haCostPrice"
            >
              <NumberInput
                placeholder={t('Transport.HaCostPrice', 'Ha cost price')}
              />
            </Form.Item>

            <Form.Item
              label={t('Transport.HaSellPrice', 'Ha sell price')}
              name="haSellPrice"
            >
              <NumberInput
                placeholder={t('Transport.HaSellPrice', 'Ha sell price')}
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Drawer>
  );
};

export default ImplementDrawer;
